import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { IoArrowBack } from "react-icons/io5";
import { Lock, Search, Clock, Plus, X, AlertTriangle } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { showStatusToast } from "../../components/ui/Toast";
import { useEtatGlobal } from "../EtatGlobal";
import ParametresKycDiligences, { API_DILIGENCES } from "./ParametresKycDiligences";

// Paramètres KYC/LAB : référentiel commun au cabinet (5 listes) servant à
// l'évaluation des risques du module KYC — reprise de la maquette Claude
// Design (kyc-params.jsx). Modification réservée aux personnes ayant accès au
// menu Paramétrage (module « Admin ») — contrôlé aussi côté serveur, voir
// kycReferentielController.
const API = "https://backend-myalfa.vercel.app/api/gi/kyc-referentiel";

const LISTES = [
  { id: "formes", label: "Formes juridiques", axe: "Client" },
  { id: "activites", label: "Activités", axe: "Activité" },
  { id: "pays", label: "Pays et territoires", axe: "Localisation" },
  { id: "missions", label: "Missions", axe: "Mission" },
  { id: "questions", label: "Questions et règles", axe: "Tous axes" },
  // Catalogue de diligences (onglet à part, voir ParametresKycDiligences.js)
  { id: "diligences", label: "Diligences", axe: "Diligences" },
];
// Listes dont les entrées portent des tags lus par les règles du catalogue.
const LISTES_TAGS = ["activites", "missions"];
const NIVEAUX = { faible: "Faible", moyen: "Moyen", eleve: "Élevé" };
const NIVEAU_STYLE = {
  faible: "bg-emerald-50 text-emerald-700 border-emerald-200",
  moyen: "bg-amber-50 text-amber-700 border-amber-200",
  eleve: "bg-rose-50 text-rose-700 border-rose-200",
  none: "bg-white text-slate-400 border-slate-200",
};
const EFFETS = { facteur: "Facteur", precision: "Précision", mesure: "Mesure spécifique", obstacle: "Obstacle", "": "Non paramétré" };
const AXES = { client: "Client", activite: "Activité", localisation: "Localisation", mission: "Mission", acceptation: "Acceptation (hors score)", global: "Global" };
const PAGE = 80;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

// Cellule texte enregistrée à la sortie du champ (Entrée = valider, Échap = annuler).
function CelluleTexte({ value, disabled, placeholder, onCommit }) {
  const [v, setV] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => setV(value || ""), [value]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [v]);
  if (disabled) return <span className="text-xs text-slate-600 whitespace-pre-line">{value || "—"}</span>;
  return (
    <textarea
      ref={ref}
      rows={1}
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v.trim() !== (value || "") && onCommit(v.trim())}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
        if (e.key === "Escape") {
          setV(value || "");
          e.target.blur();
        }
      }}
      className="w-full resize-none overflow-hidden bg-transparent border border-transparent hover:border-slate-200 focus:border-[#840040]/40 focus:bg-white rounded px-1.5 py-1 text-xs text-slate-700 focus:outline-none"
    />
  );
}

function SelectNiveau({ value, disabled, onChange }) {
  return (
    <select
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className={`w-full rounded-md border px-1.5 py-1 text-xs font-medium focus:outline-none disabled:cursor-default ${NIVEAU_STYLE[value || "none"]}`}
    >
      <option value="">—</option>
      {Object.entries(NIVEAUX).map(([k, l]) => (
        <option key={k} value={k}>{l}</option>
      ))}
    </select>
  );
}

// Tags d'une activité / mission (BTP, ESPECES, CREATION_REPRISE…), lus par
// les règles du catalogue de diligences (champ TAGS).
function CelluleTags({ tags, disabled, definitions, onChange }) {
  const [v, setV] = useState("");
  const liste = tags || [];
  const ajouter = () => {
    const t = v.trim().toUpperCase().replace(/\s+/g, "_");
    setV("");
    if (t && !liste.includes(t)) onChange([...liste, t]);
  };
  if (disabled && !liste.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 px-1.5 pt-0.5">
      {liste.map((t) => (
        <span key={t} title={definitions?.[t] || t} className="inline-flex items-center gap-1 rounded-full bg-[#840040]/10 text-[#840040] px-1.5 py-0.5 text-[10px] font-semibold">
          {t}
          {!disabled && (
            <button onClick={() => onChange(liste.filter((x) => x !== t))} aria-label={`Retirer le tag ${t}`} className="hover:text-rose-600">
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <>
          <input
            value={v}
            list="kyc-tags"
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ajouter()}
            onBlur={ajouter}
            placeholder="+ tag"
            aria-label="Ajouter un tag"
            className="w-20 bg-transparent border border-transparent hover:border-slate-200 focus:border-[#840040]/40 rounded px-1 py-0.5 text-[10px] focus:outline-none"
          />
        </>
      )}
    </div>
  );
}

const SELECT_CLS =
  "w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-700 focus:outline-none disabled:bg-transparent disabled:border-transparent disabled:appearance-none";

function ParametresKycLab({ onCancel }) {
  // Référentiel global (EtatGlobal) tenu à jour après chaque modification :
  // les recherches de la fiche client et du module KYC suivent sans recharger.
  const { patchKycReferentielEntree, kycReferentiel, kycDiligences, setKycDiligences } = useEtatGlobal();
  // Affichage immédiat avec le référentiel déjà chargé au démarrage de
  // l'application (EtatGlobal) ; le chargement complet (historique des
  // versions, droit de modification) suit sans bloquer l'écran.
  const [data, setData] = useState(() =>
    kycReferentiel ? { listes: kycReferentiel.listes, version: kycReferentiel.version, versions: [], peutModifier: false, partiel: true } : null
  );
  const listesDejaAffichees = useRef(!!kycReferentiel);
  const [erreur, setErreur] = useState(null);
  const [liste, setListe] = useState("formes");
  const [recherche, setRecherche] = useState("");
  const [filtreNiveau, setFiltreNiveau] = useState("");
  const [limite, setLimite] = useState(PAGE);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  // Catalogue de diligences (onglet 6) : celui chargé au démarrage de
  // l'application (EtatGlobal) s'affiche tout de suite, rafraîchi en
  // arrière-plan ; les modifications de l'écran mettent à jour cette même
  // copie. Son échec n'empêche pas d'utiliser les cinq listes.
  const [erreurCat, setErreurCat] = useState(null);
  const cat = erreurCat && !kycDiligences ? { erreur: erreurCat, diligences: [], regles: [] } : kycDiligences;
  const setCat = setKycDiligences;

  const charger = async () => {
    setErreur(null);
    httpClient
      .get(API_DILIGENCES)
      .then(({ data: c }) => {
        setErreurCat(null);
        setCat(c);
      })
      .catch((err) => {
        console.error("Erreur chargement catalogue de diligences :", err);
        setErreurCat("Le catalogue de diligences n'a pas pu être chargé. Réessayez dans un instant.");
      });
    try {
      const { data: d } = await httpClient.get(API);
      setData(d);
    } catch (err) {
      console.error("Erreur chargement paramètres KYC/LAB :", err);
      // Listes déjà affichées (référentiel du démarrage) : on les garde.
      if (!listesDejaAffichees.current) setErreur("Le référentiel KYC/LAB n'a pas pu être chargé. Réessayez dans un instant.");
    }
  };
  // Version publiée depuis l'onglet du catalogue : même historique.
  const ajouterVersion = (v) => setData((d) => (d ? { ...d, version: v.version, versions: [v, ...d.versions] } : d));

  // Chargement à l'ouverture seulement (charger utilise le setter du
  // catalogue global, stable).
  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => setLimite(PAGE), [liste, recherche, filtreNiveau]);

  const peutModifier = !!data?.peutModifier;
  const estQuestions = liste === "questions";
  const estCatalogue = liste === "diligences";

  const lignes = useMemo(() => {
    if (!data || liste === "diligences") return [];
    const q = recherche.trim().toLowerCase();
    return (data.listes[liste] || []).filter(
      (e) =>
        (!estQuestions || e.inRules) &&
        (!q || `${e.code} ${e.libelle} ${e.justification || ""} ${(e.tags || []).join(" ")}`.toLowerCase().includes(q)) &&
        (!filtreNiveau || (filtreNiveau === "none" ? !e.niveau : e.niveau === filtreNiveau))
    );
  }, [data, liste, recherche, filtreNiveau, estQuestions]);

  const remplacerEntree = (entree) =>
    setData((d) => ({
      ...d,
      listes: { ...d.listes, [entree.liste]: d.listes[entree.liste].map((e) => (e.id === entree.id ? entree : e)) },
    }));

  // Optimiste : la cellule change immédiatement, la version est publiée en
  // arrière-plan (toast) ; retour à la valeur précédente en cas d'échec.
  const modifier = (entree, patch) => {
    if (Object.keys(patch).every((k) => patch[k] === entree[k])) return;
    remplacerEntree({ ...entree, ...patch });
    const toast = showStatusToast({ status: "loading", titre: "Enregistrement...", message: `${entree.code} — publication d'une nouvelle version.` });
    httpClient
      .patch(`${API}/${encodeURIComponent(entree.id)}`, patch)
      .then(({ data: res }) => {
        remplacerEntree(res.entree);
        patchKycReferentielEntree(res.entree, res.version?.version);
        if (res.version) {
          setData((d) => ({ ...d, version: res.version.version, versions: [res.version, ...d.versions] }));
          toast.update({ status: "success", titre: `Version ${res.version.version} publiée`, message: `${entree.code} modifié.` });
        } else {
          toast.update({ status: "success", titre: "Aucune modification", message: "" });
        }
      })
      .catch((err) => {
        console.error("Erreur modification référentiel KYC/LAB :", err);
        remplacerEntree(entree);
        toast.update({
          status: "error",
          titre: "Erreur",
          message: err?.response?.status === 403 ? "Modification réservée aux personnes habilitées." : "La modification n'a pas pu être enregistrée.",
        });
      });
  };

  const questionsRetirees = useMemo(
    () => (data?.listes.questions || []).filter((e) => !e.inRules && e.axe !== "global"),
    [data]
  );

  const enTete = (
    <div className="flex items-center gap-3 mb-1">
      <button onClick={onCancel} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Retour">
        <IoArrowBack size={18} />
      </button>
      <h1 className="text-2xl font-bold text-gray-900 m-0">Paramètres KYC/LAB</h1>
      {data && (
        <span className="text-xs font-semibold bg-[#840040]/10 text-[#840040] rounded-full px-2.5 py-1">v{data.version}</span>
      )}
    </div>
  );

  if (erreur) {
    return (
      <div>
        {enTete}
        <p className="text-sm text-rose-600 mt-6">{erreur}</p>
        <button onClick={charger} className="mt-3 h-8 px-3 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {enTete}
      <p className="text-sm text-gray-400 mb-5 ml-10">
        Référentiel commun au cabinet pour l'évaluation des risques LCB-FT (axes Client, Activité, Localisation, Mission).
      </p>

      {data && !data.partiel && !peutModifier && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
          <Lock className="w-3.5 h-3.5" />
          Lecture seule : le paramétrage est réservé aux personnes ayant accès au menu Paramétrage.
        </div>
      )}

      {/* Tags proposés à la saisie (activités, missions) */}
      <datalist id="kyc-tags">
        {Object.entries(cat?.tags || {}).map(([t, def]) => (
          <option key={t} value={t}>{def}</option>
        ))}
      </datalist>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {LISTES.map((l) => {
          const nb =
            l.id === "diligences"
              ? cat && !cat.erreur
                ? cat.diligences.filter((d) => d.etat !== "archive").length
                : null
              : data
                ? (data.listes[l.id] || []).filter((e) => l.id !== "questions" || e.inRules).length
                : null;
          return (
            <button
              key={l.id}
              onClick={() => setListe(l.id)}
              className={`flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                liste === l.id ? "border-[#840040] text-[#840040] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {l.label}
              {nb != null && <span className="text-[11px] bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">{nb}</span>}
            </button>
          );
        })}
      </div>

      {estCatalogue ? (
        <ParametresKycDiligences cat={cat} setCat={setCat} onVersion={ajouterVersion} />
      ) : (
      <>
      {/* Barre d'outils */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Code, libellé, justification, tag…"
            className="h-9 w-72 pl-8 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#840040]/20"
          />
        </div>
        <select
          value={filtreNiveau}
          onChange={(e) => setFiltreNiveau(e.target.value)}
          className="h-9 px-2 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white"
        >
          <option value="">Tous niveaux</option>
          {Object.entries(NIVEAUX).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
          <option value="none">Non paramétré</option>
        </select>
        {estQuestions && peutModifier && questionsRetirees.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setAjoutOuvert((o) => !o)}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm border border-[#840040] text-[#840040] hover:bg-[#840040] hover:text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une question
            </button>
            {ajoutOuvert && (
              <div
                className="absolute z-20 mt-1 w-[480px] max-h-80 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-100 py-1"
                onMouseLeave={() => setAjoutOuvert(false)}
              >
                {questionsRetirees.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => {
                      modifier(e, { inRules: true });
                      setAjoutOuvert(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex gap-2"
                  >
                    <b className="text-xs text-slate-700 w-10 shrink-0">{e.code}</b>
                    <span className="text-xs text-slate-600">
                      {e.libelle}
                      <span className="block text-[10px] text-slate-400">{e.categorie}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <span className="ml-auto text-xs text-slate-400">
          {lignes.length} ligne{lignes.length > 1 ? "s" : ""}
        </span>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {!data ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-6 rounded bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full text-sm table-fixed min-w-[760px]">
            {estQuestions ? (
              <colgroup>
                <col style={{ width: 70 }} />
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 40 }} />
              </colgroup>
            ) : (
              <colgroup>
                <col style={{ width: 90 }} />
                <col style={{ width: "42%" }} />
                <col />
                <col style={{ width: 110 }} />
              </colgroup>
            )}
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 text-left">
                <th className="px-3 py-2 font-semibold">Code</th>
                {estQuestions ? (
                  <>
                    <th className="px-3 py-2 font-semibold">Question</th>
                    <th className="px-3 py-2 font-semibold">Axe</th>
                    <th className="px-3 py-2 font-semibold">Déclenche</th>
                    <th className="px-3 py-2 font-semibold">Effet</th>
                    <th className="px-3 py-2 font-semibold">Niveau</th>
                    <th />
                  </>
                ) : (
                  <>
                    <th className="px-3 py-2 font-semibold">Libellé</th>
                    <th className="px-3 py-2 font-semibold">Description / justification</th>
                    <th className="px-3 py-2 font-semibold">Niveau</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lignes.slice(0, limite).map((e) => {
                const alertes = Array.isArray(e.checks) ? e.checks : [];
                return (
                  <tr key={e.id} className="align-top hover:bg-slate-50/50">
                    <td className="px-3 py-1.5 text-xs font-semibold text-slate-700">
                      <div className="flex items-center gap-1">
                        {e.code}
                        {alertes.length > 0 && (
                          <span title={alertes.join("\n")}>
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                          </span>
                        )}
                      </div>
                    </td>
                    {estQuestions ? (
                      <>
                        <td className="px-3 py-1.5">
                          <p className="text-xs text-slate-800 m-0">{e.libelle}</p>
                          <p className="text-[10px] text-slate-400 m-0">{e.categorie}</p>
                        </td>
                        <td className="px-2 py-1.5">
                          <select disabled={!peutModifier} value={e.axe} onChange={(x) => modifier(e, { axe: x.target.value })} className={SELECT_CLS}>
                            {Object.entries(AXES).map(([k, l]) => (
                              <option key={k} value={k}>{l}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            disabled={!peutModifier}
                            value={e.declenche || ""}
                            onChange={(x) => modifier(e, { declenche: x.target.value || null })}
                            className={SELECT_CLS}
                          >
                            <option value="">—</option>
                            <option value="oui">Oui</option>
                            <option value="non">Non</option>
                            {e.declenche && !["oui", "non"].includes(e.declenche) && <option value={e.declenche}>{e.declenche}</option>}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            disabled={!peutModifier}
                            value={e.effet || ""}
                            onChange={(x) => modifier(e, { effet: x.target.value || null })}
                            className={SELECT_CLS}
                          >
                            {Object.entries(EFFETS).map(([k, l]) => (
                              <option key={k} value={k}>{l}</option>
                            ))}
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-1.5">
                          <CelluleTexte value={e.libelle} disabled={!peutModifier} onCommit={(v) => v && modifier(e, { libelle: v })} />
                          {e.liste === "pays" && e.nationalite && <p className="text-[10px] text-slate-400 m-0 px-1.5">{e.nationalite}</p>}
                          {e.liste === "missions" && e.typeMission && <p className="text-[10px] text-slate-400 m-0 px-1.5">{e.typeMission}</p>}
                          {LISTES_TAGS.includes(e.liste) && (
                            <CelluleTags tags={e.tags} disabled={!peutModifier} definitions={cat?.tags} onChange={(tags) => modifier(e, { tags })} />
                          )}
                          {e.liste === "formes" && e.typeClient && <p className="text-[10px] text-slate-400 m-0 px-1.5">{e.typeClient}</p>}
                        </td>
                        <td className="px-2 py-1.5">
                          <CelluleTexte
                            value={e.justification}
                            placeholder="—"
                            disabled={!peutModifier}
                            onCommit={(v) => modifier(e, { justification: v || null })}
                          />
                        </td>
                      </>
                    )}
                    <td className="px-2 py-1.5">
                      <SelectNiveau value={e.niveau} disabled={!peutModifier} onChange={(v) => modifier(e, { niveau: v })} />
                    </td>
                    {estQuestions && (
                      <td className="px-1 py-1.5 text-center">
                        {peutModifier && (
                          <button
                            title="Retirer des règles"
                            onClick={() => modifier(e, { inRules: false })}
                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {lignes.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">
                    Aucune ligne ne correspond à la recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {lignes.length > limite && (
          <div className="p-3 text-center border-t border-slate-100">
            <button onClick={() => setLimite((l) => l + 200)} className="h-8 px-3 rounded-lg text-xs font-medium border border-slate-300 hover:bg-slate-50">
              Afficher plus ({lignes.length - limite} restantes)
            </button>
          </div>
        )}
      </div>
      </>
      )}

      {/* Historique des versions */}
      {data && (
        <div className="bg-white rounded-xl border border-slate-200 mt-5 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2 m-0">
            <Clock className="w-4 h-4 text-[#840040]" />
            Historique des versions
          </h3>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {data.partiel && <p className="text-xs text-slate-400 m-0 py-2">Chargement de l'historique…</p>}
            {data.versions.map((v) => (
              <div key={v.version} className="grid grid-cols-[50px_170px_1fr] gap-3 py-2 text-xs">
                <b className="text-slate-800">v{v.version}</b>
                <span className="text-slate-500">
                  {fmtDate(v.createdAt)} · {v.auteur || "—"}
                </span>
                <span className="text-slate-600">{v.note || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParametresKycLab;
